import { type RefObject, useEffect, useRef, useState } from "react";

export interface UseListboxOption {
  value: string;
}

export interface UseListboxArgs<T extends UseListboxOption> {
  id: string;
  options: T[];
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** The listbox panel, when it's portaled outside `containerRef` (Select): clicks inside it must not count as outside. */
  panelRef?: RefObject<HTMLElement | null>;
}

export function useListbox<T extends UseListboxOption>({
  id,
  options,
  value,
  onChange,
  disabled = false,
  panelRef,
}: UseListboxArgs<T>) {
  const selectedIndex = options.findIndex((option) => option.value === value);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(selectedIndex >= 0 ? selectedIndex : 0);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const listboxId = `${id}-listbox`;
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!containerRef.current?.contains(target) && !panelRef?.current?.contains(target)) setIsOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen, panelRef]);

  useEffect(() => {
    if (isOpen) optionRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [isOpen, highlightedIndex]);

  const commitSelection = (index: number) => {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const openAt = (index: number) => {
    setHighlightedIndex(Math.max(0, Math.min(index, options.length - 1)));
    setIsOpen(true);
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        isOpen
          ? setHighlightedIndex((i) => Math.min(i + 1, options.length - 1))
          : openAt(selectedIndex >= 0 ? selectedIndex : 0);
        break;
      case "ArrowUp":
        event.preventDefault();
        isOpen
          ? setHighlightedIndex((i) => Math.max(i - 1, 0))
          : openAt(selectedIndex >= 0 ? selectedIndex : options.length - 1);
        break;
      case "Home":
        if (isOpen) {
          event.preventDefault();
          setHighlightedIndex(0);
        }
        break;
      case "End":
        if (isOpen) {
          event.preventDefault();
          setHighlightedIndex(options.length - 1);
        }
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        isOpen ? commitSelection(highlightedIndex) : openAt(selectedIndex >= 0 ? selectedIndex : 0);
        break;
      case "Escape":
        if (isOpen) {
          event.preventDefault();
          setIsOpen(false);
        }
        break;
      case "Tab":
        setIsOpen(false);
        break;
    }
  };

  return {
    isOpen,
    setIsOpen,
    highlightedIndex,
    setHighlightedIndex,
    selectedIndex,
    containerRef,
    triggerRef,
    optionRefs,
    listboxId,
    optionId,
    openAt,
    commitSelection,
    handleTriggerKeyDown,
  };
}
